import React, { useState, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import selectors from '@state/selectors';
import {IProps, IState, IMultiEditingPickerCategory, IMultiEditingPickerData, IMultiEditingPickerRow} from './types';
import * as S from './styles';
import C from '@assets/themes/colors';
import { Context } from "routes/private/Editor/Context";
import MultiEditingCategories from './categories';
import MultiEditingGroups from './groups';
import Accordion from '@components/Accordion';
import { IDSConfig, IMultiEditingConfig } from 'state/dsconfig/types';
import { getCategoryElementTagged } from '@utils/selectDescribe';
import { findSelectedSetName, findSetsNames, findSetsGroupsElementsIds } from '@utils/sets';
import ComboBox from '@components/ComboBox';
import { findElementById } from '@utils/editor';
import SetsMenu from './SetsMenu';
import { compareMultiEditingProps } from '@utils/memo';

const MultiEditingPicker:React.FC<IProps> = React.memo((props) => {
  
  const {
    data,
    multiEditingConfig,
    editorStatus,
    selectDescribe,
    onSelectorActive,
    onSetsChange,
    onCommandChange,
    onMultiEditingFocus,
  } = props;

  const [dataIndex, setDataIndex] = useState(-1);
  const [multiEditingPickerData,setMultiEditingPickerData] = useState<IMultiEditingPickerData>();
  const [setsName, setSetsName] = useState(findSelectedSetName(data)??"");
  const [sets, setSets] = useState<string[]>([]);
  const [openSetsMenu, setOpenSetsMenu] = useState({status: false, title: ""});
  
  const dsConfig: IDSConfig | undefined =  useSelector(selectors.dsConfig.getDSConfig);

  const updateSets = () => {
    const newSets = findSetsNames(data) ?? [];
    setSets(newSets.sort());
  }

  useEffect(() => {
    onSetsChange(setsName);
  }, [setsName])

  useEffect(() => {
    updateSets();
  }, [JSON.stringify(data)]);

  useEffect(() => {
    if (multiEditingConfig && editorStatus.currentPicker === "MultiEditing") {
      const multiEditingData = getMultiEditingDataForPicker(multiEditingConfig);      
      setMultiEditingPickerData(multiEditingData);
      setDataIndex(-1);
      onMultiEditingFocus([]);
    }
  }, [editorStatus.currentPicker, multiEditingConfig])

  function fieldInCategory(category: IMultiEditingPickerCategory, field: string): boolean {
    if (category.selectDescribeFields.includes(field)) return true;
    return false;
  }

  useEffect(() => {
    if (editorStatus.selectedElementsId !== undefined && editorStatus.selectedElementsId.length > 0 && multiEditingPickerData !== undefined && multiEditingPickerData.multiEditingPickerRows !== undefined) {
      let index = -1;
      let dif = false;
      for (let j=0;j<editorStatus.selectedElementsId.length;j++) {
        const id = editorStatus.selectedElementsId[j];
        const fieldTagged = getCategoryElementTagged(id, selectDescribe?.taggedFields??[]);
        if (fieldTagged !== undefined) {
          for (let i=0;i<((multiEditingPickerData?.multiEditingPickerRows.length)??0);i++) {
            let found: boolean = false;
            if (multiEditingPickerData?.multiEditingPickerRows[i].multiEditingPickerGroup!==undefined || multiEditingPickerData.multiEditingPickerRows[i].multiEditingPickerGroup?.multiEditingPickerCategories !== undefined) {
              for (let a=0;a<((multiEditingPickerData.multiEditingPickerRows[i].multiEditingPickerGroup?.multiEditingPickerCategories.length)??0);a++) {
                if (fieldInCategory(multiEditingPickerData.multiEditingPickerRows[i].multiEditingPickerGroup?.multiEditingPickerCategories[a]!, fieldTagged!)) {
                  if (index !== -1 && index !== i && dif === false) {
                    dif = true;
                  }
                  index = i;
                  found = true;
                  break;
                }
              }
            }
            if (!found && multiEditingPickerData.multiEditingPickerRows[i].multiEditingPickerCategory !== undefined) {
              if (fieldInCategory(multiEditingPickerData.multiEditingPickerRows[i].multiEditingPickerCategory!, fieldTagged)) {
                if (index !== -1 && index !== i && dif === false) {
                  dif = true;
                }
                index = i;
                found = true;
              }
            }
            if (found) break;
          }
        }
        else {
          dif = true;
        }
      }
      if (dif) setDataIndex(-1);
      else setDataIndex(index);
    }
  }, [editorStatus.selectedElementsId])

  const getMultiEditingDataForPicker = (multiEditingConfig: IMultiEditingConfig): IMultiEditingPickerData => {
    const categoriesForPicker: IMultiEditingPickerCategory[] = multiEditingConfig.categories.map((category) => { return {id: category.id, name: category.name, groupId: category.groupId,selectDescribeFields:category.fields.map((field) => { return field.selectDescribeField})}});
    let groupsAdded: string[] = [];
    let multiEditingData: IMultiEditingPickerData = {multiEditingPickerRows: []};
    for (let i=0;i<categoriesForPicker.length;i++) {
      let multiEditingDataRow: IMultiEditingPickerRow = {}
      if (categoriesForPicker[i].groupId) {
        if (!groupsAdded.includes(categoriesForPicker[i].groupId!)) {
          const groupName = getGroupName(categoriesForPicker[i].groupId!, multiEditingConfig);
          const groupCategories = getGroupCategories(categoriesForPicker[i].groupId!, categoriesForPicker);
          multiEditingDataRow.multiEditingPickerGroup = {id: categoriesForPicker[i].groupId!, name: groupName, multiEditingPickerCategories: groupCategories};
          groupsAdded.push(categoriesForPicker[i].groupId!);
          multiEditingData.multiEditingPickerRows.push(multiEditingDataRow);
        }
      }
      else {
          multiEditingDataRow.multiEditingPickerCategory = {id: categoriesForPicker[i].id, name: categoriesForPicker[i].name, selectDescribeFields: categoriesForPicker[i].selectDescribeFields};
          multiEditingData.multiEditingPickerRows.push(multiEditingDataRow);
        }
    }
    return multiEditingData;
  }

  const getGroupName = (groupId: string, multiEditingConfig: IMultiEditingConfig): string => {
    for (let i=0;i<multiEditingConfig.groups.length;i++) {
      if (multiEditingConfig.groups[i].id === groupId) {
        return multiEditingConfig.groups[i].name;
      }
    }
    return ""; 
  }

  const getGroupCategories = (groupId: string, allCategories: IMultiEditingPickerCategory[]): IMultiEditingPickerCategory[] => {
    let groupCategories: IMultiEditingPickerCategory[] = [];
    for (let i=0;i<allCategories.length;i++) {
      if (allCategories[i].groupId && allCategories[i].groupId === groupId) {
        groupCategories.push({id: allCategories[i].id, name: allCategories[i].name, groupId: allCategories[i].groupId, selectDescribeFields: allCategories[i].selectDescribeFields});
      }
    }
    return groupCategories; 
  }

  const getSelectDescribeFields = (row: IMultiEditingPickerRow): string[] => {
    if (row.multiEditingPickerCategory) {
      return row.multiEditingPickerCategory.selectDescribeFields;
    }
    else {
      let selectDescribeFiels: string[] = [];
      for (let i=0;i<row.multiEditingPickerGroup!.multiEditingPickerCategories.length;i++) {
        selectDescribeFiels = selectDescribeFiels.concat(row.multiEditingPickerGroup!.multiEditingPickerCategories[i].selectDescribeFields);
      }
      return selectDescribeFiels;
    }
  }

  const getSelectedSets = (value: string) => {
    let elementsIds: string[] = findSetsGroupsElementsIds(data);
    let hiddes = new Map<string, string>();
    for (let i = 0; i < elementsIds.length; i++) {
      const element = findElementById(data, elementsIds[i]);
      let hide = "true";
      if (element && element.name && element.name.toLowerCase() == value.toLowerCase()) {
        hide = "false";
      }
      hiddes.set(elementsIds[i], hide);
    }
    return hiddes;
  }

  const handleSelectSet = (id: string, value: string) => {
    if (id === "setsVariations") {
      const sets = getSelectedSets(value);
      onCommandChange("hideUnhide", undefined, Array.from(sets.keys()), Array.from(sets.values()));
      setSetsName(value);
    }
  }

  const handleOpenSetsMenu = (id: string, option: string) => {
    const sets = getSelectedSets(option);
    const setEntries = Array.from(sets.entries());
    onSelectorActive("", setEntries.filter(se => se[1] === "false").map(se => se[0]));
    setOpenSetsMenu({status: true, title: option});
  }

  const handleCloseSetsMenu = () => {
    setOpenSetsMenu({status: false, title: ""});
  }

  return (
    <S.Root>
      {
        openSetsMenu.status &&
        <SetsMenu
          title={openSetsMenu.title}
          setNames={sets}
          selectedSetName={setsName}
          handleClose={handleCloseSetsMenu}
        />
      }
      {
        sets && sets.length>0 && 
        <>
          <S.SetsRow>
            <S.SetsTitle>Template Variations</S.SetsTitle>
            <ComboBox
            id="setsVariations"
            placeholder=""
            options={sets}
            required={false}
            disabled={false}
            position={"bottom"}
            chooseOption={handleSelectSet}
            openOptionMenu={dsConfig?.editorConfig.multiSetsOption ? handleOpenSetsMenu : undefined}
            value={setsName}
            containerStyle={{
                padding: "0px",
                width: "100%",
            }}
            optionContainerStyle={{
                padding: `0px 0px`,
            }}
            optionStyle={{
                fontSize: `16px`,
                color: `#272727`,
                padding: `10px 14px`,
            }}
            style={{
                fontSize: `14px`,
                color: `${C.BLUE}`,
                fontFamily: `Roboto`,
                padding: `0px 6px`,
            }}
            titleStyle={{
            }}
            />
          </S.SetsRow>
          <S.LineSeparatorSets/>
        </>
      }
      {
        multiEditingPickerData?.multiEditingPickerRows.map((dataRow, index) => {
          return (
            <Accordion
              key={index}
              index={index}
              title={dataRow.multiEditingPickerGroup?dataRow.multiEditingPickerGroup.name!:dataRow.multiEditingPickerCategory?.name!}
              active={dataIndex === index}
              activeColor = '#ff7319'
              hoverHighlight={true} 
              onClick={()=> {
                if (index!==dataIndex){
                  setDataIndex(index);
                  onMultiEditingFocus(getSelectDescribeFields(dataRow));
                } 
                else setDataIndex(-1);
              }}>
                {
                  dataIndex === index &&
                  <>
                    {
                      dataRow.multiEditingPickerGroup &&
                      <MultiEditingGroups key={index} id={dataRow.multiEditingPickerGroup.id} name={dataRow.multiEditingPickerGroup.name} categories={dataRow.multiEditingPickerGroup.multiEditingPickerCategories.map((multiEditingCategory)=> { return {id: multiEditingCategory.id, name: multiEditingCategory.name}})}/>
                    }
                    {
                      dataRow.multiEditingPickerCategory &&
                      <MultiEditingCategories key={index} id={dataRow.multiEditingPickerCategory.id} name={dataRow.multiEditingPickerCategory.name}/>
                    }
                  </>
                }
            </Accordion>
          )
        })
      }
    </S.Root>
  );
}, compareMultiEditingProps);

export default MultiEditingPicker;